import { useState, useMemo } from "react";
import { format } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { TeacherTrialStatsTable } from "../TeacherTrialStatsTable";
import { TrialStatsFilters, TrialStatsFiltersState } from "../TrialStatsFilters";
import { useTeacherTrialStats } from "@/hooks/useTeacherTrialStats";
import { useTrialStatusCounts } from "@/hooks/useTrialStatusCounts";
import { Users, CheckCircle, Percent } from "lucide-react";

export const TrialStatsTab = () => {
  const [filters, setFilters] = useState<TrialStatsFiltersState>({});

  // Convert Date to ISO string for queries
  const queryFilters = useMemo(() => ({
    startDate: filters.startDate ? format(filters.startDate, 'yyyy-MM-dd') : undefined,
    endDate: filters.endDate ? format(filters.endDate, 'yyyy-MM-dd') : undefined,
    status: filters.status,
  }), [filters]);

  const { data: statusCounts = [] } = useTrialStatusCounts({
    startDate: queryFilters.startDate,
    endDate: queryFilters.endDate,
  });

  const totalCount = useMemo(() => 
    statusCounts.reduce((sum, item) => sum + item.count, 0),
    [statusCounts]
  );

  const { data: trialStats, isLoading } = useTeacherTrialStats(queryFilters);

  const summaryStats = useMemo(() => {
    if (!trialStats || trialStats.length === 0) {
      return { totalTrials: 0, totalSuccess: 0, avgSuccessRate: 0 };
    }

    const totalTrials = trialStats.reduce((sum, t) => sum + t.total_trials, 0);
    const totalSuccess = trialStats.reduce((sum, t) => sum + t.successful_trials, 0);
    const avgSuccessRate = totalTrials > 0 ? (totalSuccess / totalTrials) * 100 : 0;

    return { totalTrials, totalSuccess, avgSuccessRate };
  }, [trialStats]);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">รวมทดลองเรียน</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summaryStats.totalTrials} ครั้ง</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">สำเร็จ (ปิดการขาย)</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{summaryStats.totalSuccess} ครั้ง</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">อัตราสำเร็จเฉลี่ย</CardTitle>
            <Percent className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summaryStats.avgSuccessRate.toFixed(1)}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Table */}
      <Card>
        <CardHeader>
          <CardTitle>ทดลองเรียน</CardTitle>
          <CardDescription>
            สถิติการสอนทดลองเรียนของครูแต่ละคน และอัตราสำเร็จ (สถานะ "ปิดการขายเรียบร้อย")
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TrialStatsFilters
            filters={filters}
            onFiltersChange={setFilters}
            statusCounts={statusCounts}
            totalCount={totalCount}
            isLoading={isLoading}
          />
          <TeacherTrialStatsTable 
            data={trialStats || []} 
            isLoading={isLoading} 
            startDate={queryFilters.startDate}
            endDate={queryFilters.endDate}
          />
        </CardContent>
      </Card>
    </div>
  );
};
